/**
 * Passkey / WebAuthn login button.
 *
 * ADR-118 #18: the WebAuthn ceremony + allauth round-trip
 * lives inside `allauthAdapter.loginWithPasskey` (the lib runs
 * `navigator.credentials.get(...)` internally and reports success
 * via `LoginResult`). This component only invokes
 * `actions.loginWithPasskey()` and surfaces UI state.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, HStack } from '@chakra-ui/react';
import { HiFingerPrint } from 'react-icons/hi2';
import { Button } from '@themes/aegix';
import {
  useAuthActions,
  type AllauthUser,
} from '@aegix/auth-identity';

interface PasskeyButtonProps {
  onSuccess: () => void;
  onError: (message: string) => void;
}

export function PasskeyButton({ onSuccess, onError }: PasskeyButtonProps) {
  const { t } = useTranslation();
  const actions = useAuthActions<AllauthUser>();
  const [loading, setLoading] = useState(false);

  // Only show if WebAuthn is supported AND the adapter advertises
  // passkey login (it does for allauth; future adapter swaps that
  // drop passkey support would hide the button automatically).
  if (!window.PublicKeyCredential || !actions.loginWithPasskey) {
    return null;
  }

  const handlePasskeyLogin = async () => {
    setLoading(true);
    try {
      await actions.loginWithPasskey!();
      onSuccess();
    } catch (err) {
      onError(
        err instanceof Error
          ? err.message
          : t('passkey.failed'),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      visual="secondary"
      onClick={handlePasskeyLogin}
      disabled={loading}
      style={{ width: '100%' }}
    >
      <HStack gap={2} justify="center">
        <HiFingerPrint size={18} />
        <Text fontSize="sm">
          {loading ? t('passkey.verifying') : t('passkey.signInWithPasskey')}
        </Text>
      </HStack>
    </Button>
  );
}
